export type KeyOf<T> = string | number | symbol;

export const buildMapByKey = <T, K extends KeyOf<T>>(arr: T[], getKey: (item: T) => K): Map<K, T> => {
  const m = new Map<K, T>();
  for (const item of arr) {
    m.set(getKey(item), item);
  }
  return m;
};

// Deduplicate by key, keep the last occurrence and preserve order
export const dedupeLastByKey = <T, K extends KeyOf<T>>(arr: T[], getKey: (item: T) => K): T[] => {
  if (arr.length <= 1) return arr.slice();
  const seen = new Set<K>();
  const dedupedReversed: T[] = [];
  for (let i = arr.length - 1; i >= 0; i--) {
    const item = arr[i];
    const key = getKey(item);
    if (!seen.has(key)) {
      seen.add(key);
      dedupedReversed.push(item);
    }
  }
  return dedupedReversed.reverse();
};

export const patchMapWithUpsert = <T, K extends KeyOf<T>>(map: Map<K, T>, updates: T[], getKey: (item: T) => K): void => {
  for (const upd of updates) {
    map.set(getKey(upd), upd);
  }
};

export const patchMapWithUpsertAndRemoval = <T, K extends KeyOf<T>>(
  map: Map<K, T>,
  updates: T[],
  getKey: (item: T) => K,
  shouldRemove: (updated: T) => boolean,
  beforeUpdate?: (updated: T, existing?: T) => void
): void => {
  for (const upd of updates) {
    const key = getKey(upd);
    const existingItem = map.get(key);

    if (beforeUpdate) {
      beforeUpdate(upd, existingItem);
    }

    if (shouldRemove(upd)) {
      map.delete(key);
    } else if (existingItem) {
      // Shallow-merge to keep refs stable
      Object.assign(existingItem as any, upd);
    } else {
      map.set(key, upd);
    }
  }
};
